import React from 'react'
import { Link } from 'gatsby'

import { breakpoints } from 'src/ui/dimensions'
import { styled, useTheme } from 'src/ui/theme'
import { HBox, Wrapper } from 'src/ui/layout'
import { Image } from 'src/ui/atoms'
import { Header, Body } from 'src/ui/typography'

import VisibilitySensor from 'react-visibility-sensor'
import { useSpring, animated } from 'react-spring'

const Title = styled.div`
  opacity: 0;
  position: absolute;

  right: 64px;
  bottom: 64px;
  max-width: 488px;
  padding: 0 16px;
  z-index: 2;
  text-align: right;

  transition: opacity 0.3s;
  @media (max-width: ${breakpoints.tablet}px) {
    display: none;
  }
`

const Container = styled.div`
  padding: 64px 0;

  position: relative;
  background-color: ${({ theme }) => theme.colors.white.primary};

  transition: background-color 0.3s;
  &:hover {
    background-color: ${({ theme }) => theme.colors.brand.primary};

    ${Title} {
      opacity: 1;
    }
  }
`

const Bg = styled(Image)`
  position: absolute !important;
  z-index: 0;

  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  mix-blend-mode: multiply;
`

const Content = styled(animated.div)`
  position: relative;
  padding: 40px 40px 40px;
  width: 43.9%;
  background: ${({ theme }) => theme.colors.white.primary};
  @media (max-width: ${breakpoints.tablet}px) {
    width: 100%;
  }
`

const Subtitle = styled.h5`
  font-family: 'Gillroy', sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 100%;
  letter-spacing: 2%;
  color: #373737;
`

type Props = {
  images: TImages
}

export const Learn: React.FC<Props> = ({ images }) => {
  const theme = useTheme()

  const [visible, setVisible] = React.useState(false)

  const contentStyles = useSpring({
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateX(0)' : 'translateX(-60px)',
  })

  const imageStyles = useSpring({
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateX(0)' : 'translateX(100px)',
    mixBlendMode: 'multiply',
  })

  const handler = (active: boolean) => {
    if (active) {
      setVisible(true)
    }
  }

  return (
    <VisibilitySensor onChange={handler} partialVisibility scrollDelay={2000}>
      <Container>
        <animated.div style={imageStyles}>
          <Bg fluid={images['learn']} />
        </animated.div>
        <Wrapper>
          <Content style={contentStyles}>
            <Header size="h2">Групповые и менторские стажировки</Header>
            <HBox height={24} />
            <Body>
              С&nbsp;2016&nbsp;года KODE проводит стажировки
              по&nbsp;10&nbsp;направлениям, включая мобильную и веб-разработку,
              управление проектами, аналитику, QA-тестирование и&nbsp;дизайн.
            </Body>
            <HBox />
            <Body>
              Подавать заявки на стажировку можно круглый год, мы изучим анкету
              и предложим подходящую программу в зависимости от ваших знаний и
              опыта.
            </Body>
            <HBox height={16} />
            <Subtitle>Групповая стажировка</Subtitle>
            <HBox height={16} />
            <Body>
              Традиционно стартует 2 раза в год: весной и осенью. В течении
              месяца мы учим основам разработки на кейсах KODE, проводим лекции
              и воркшопы, даем ДЗ и принимаем финальные проекты.
            </Body>
            <HBox height={16} />
            <Subtitle>Менторская стажировка</Subtitle>
            <HBox height={16} />
            <Body>
              Проводится круглый год. Ты работаешь с реальными задачами,
              прокачиваешь скиллы, и все это под руководством опытного ментора.
              А также получаешь шанс стать частью команды KODE.
            </Body>
            <HBox height={16} />
            <Body weight="medium">
              <a href="https://edu.kode.ru/" target="_blank">
                Больше о&nbsp;стажировках KODE&nbsp;→
              </a>
            </Body>
          </Content>
        </Wrapper>
        <Title>
          <Header size="h4" color={theme.colors.white.primary}>
            Проводим лекции по стажировкам в офисе KODE
          </Header>
        </Title>
      </Container>
    </VisibilitySensor>
  )
}
